import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../../hooks/hooks";
import PageLoader from "../../../../../components/PageLoader";
import Table from "../../../../../common/components/table-manager/v1.1/table";
import { dbaCatalogs } from "../../../../../redux/dba-catalogs/action";
import { selectDBACatalogsListItems } from "../../../../../redux/dba-catalogs/selector";
import { getHostName, getOrigin } from "../../../../../common/services/common.service";
import storePersist from "../../../../../redux/storePersist";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";

const DBACatalogs = ({ selectedDba }: any) => {
  const [isPublishing, setIsPublishing] = useState(false);
  const SpinnerIcon = (tooltip: string) => (
    <div title={tooltip}>
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" className="animate-spin text-white" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
      </svg>
    </div>
  );
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [pageData, setPageData] = useState(1)
  const [pageSize, setPageSize] = useState(10);
  const dbaCatalogsItems = useSelector(selectDBACatalogsListItems)
  const [columnDefs, setColumnDefs] = useState({});
  const createColumnDefs: any = () => {
    setColumnDefs([
      {
        field: 'Sl',
        sortable: false,
        filter: false,
        isSortLabel: true,
        headerName: 'Sl',
        cellRenderer(params: any) {
          return parseInt(
            params.node.id,
            10
          ) + 1;
        },
      },
      { field: 'ImageURL', headerName: 'Image', filter: false, cellRenderer: 'imageRenderer', filterType: 'text', },
      { field: 'DBAName', headerName: 'DBA Name', filter: true },
      { field: 'ProviderName', headerName: 'Provider Name', filter: true },
      { field: 'ProductName', headerName: 'Product Name', filter: true },
      { field: 'Price', headerName: 'Price', filter: true },
      { field: 'StockQuantity', headerName: 'Stock Quantity', filter: true },
      { field: 'Sku', headerName: 'SKU', filter: true },
      { field: 'IsPublished', headerName: 'Published', cellRenderer: 'yesNoRenderer', filter: true },
    ]);
  }

  const fetchDBACatalogsList = () => {
    const hostname = getHostName();
    const hostUrl = getOrigin()
    const transaction_id = uuidv4();
    const guestTokenmetaData = storePersist.get("guestTokenMetadata").current?.result?.TokenMetadata;
    dispatch(dbaCatalogs.list({
      url: `https://catalog.digiit.${process.env.REACT_APP_API_URL}/api/v6.1.2/catalog/catalog-collections/management/dbas/fetch`, entity: 'dbaCatalogs',
      jsonData: {
        Context: {
          "domain": "Commerce",
          "action": "catalog",
          "core_version": "6.0",
          "bap_id": hostname || '',
          "bap_uri": hostUrl || '',
          "bapie_id": '',
          "bapie_uri": '',
          "bapnp_id": '',
          "bapnp_uri": '',
          "bpp_id": guestTokenmetaData?.TenantId,
          "bpp_uri": guestTokenmetaData?.CompanyName,
          "bppie_id": '',
          "bppie_uri": '',
          "bppnp_id": '',
          "bppnp_uri": '',
          "transaction_id": transaction_id,
          "message_id": uuidv4(),
          "city": guestTokenmetaData?.City,
          "country": guestTokenmetaData?.Country,
          "timestamp": moment().utc(),
          "ttl": "-1"
        },
        Records: [{}],
        Pagination:
        {
          "PageNumber": pageData,
          "PageSize": 25,
          "SortField": "DBAId",
          "SortOrder": "desc",
        }
      }
    }))
  }

  useEffect(() => {
    createColumnDefs();
    fetchDBACatalogsList();
  }, [pageData, pageSize]);

  const additionButtons: any = [
    {
      svg: isPublishing ? SpinnerIcon("Publishing...") : (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" color="#ffffff" fill="none">
          <title>{selectedDba?.IsDBACatalogPublished === true || isPublishing ? "Publishing is disabled because the DBA Catalog is already published" : "Click to publish the DBA Catalog"}</title>
          <path d="M17.4776 9.01106C17.485 9.01102 17.4925 9.01101 17.5 9.01101C19.9853 9.01101 22 11.0294 22 13.5193C22 15.8398 20.25 17.7508 18 18M17.4776 9.01106C17.4924 8.84606 17.5 8.67896 17.5 8.51009C17.5 5.46695 15.0376 3 12 3C9.12324 3 6.76233 5.21267 6.52042 8.03192M17.4776 9.01106C17.3753 10.1476 16.9286 11.1846 16.2428 12.0165M6.52042 8.03192C3.98398 8.27373 2 10.4139 2 13.0183C2 15.4417 3.71776 17.4632 6 17.9273M6.52042 8.03192C6.67826 8.01687 6.83823 8.00917 7 8.00917C8.12582 8.00917 9.16474 8.38194 10.0005 9.01101" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M12 13L12 21M12 13C11.2998 13 9.99153 14.9943 9.5 15.5M12 13C12.7002 13 14.0085 14.9943 14.5 15.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>),
      disabled: selectedDba?.IsDBACatalogPublished === true || isPublishing,
      callback: () => {
        if (!isPublishing && !selectedDba?.IsDBACatalogPublished) {
          handlePublishDBACatalogs();
        }
      }
    },
    {
      svg:
        <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 98.432 98.432" width="24" height="24">
          <title>Sync updated changes from Global Catalog </title>
          <path d="M49.246,37.366v-2.035c0-0.818-0.463-1.563-1.194-1.927c-0.732-0.367-1.604-0.282-2.257,0.208l-9.804,7.403 c-0.542,0.406-0.858,1.044-0.858,1.72c0,0.674,0.316,1.311,0.858,1.717l9.804,7.399c0.653,0.489,1.525,0.571,2.257,0.207 c0.73-0.366,1.194-1.112,1.194-1.929v-1.9c8.287,0.811,15.023,6.995,16.441,15.083c0.32,1.729,1.811,2.976,3.561,2.976h3.602 c1.049,0,2.055-0.447,2.734-1.246c0.689-0.791,0.993-1.848,0.833-2.893C74.385,48.664,63.104,38.207,49.246,37.366z"></path>
          <path d="M62.447,79.344l-9.807-7.396c-0.65-0.492-1.524-0.574-2.256-0.211c-0.731,0.368-1.194,1.112-1.194,1.93v1.898 c-8.289-0.811-15.024-6.993-16.441-15.083c-0.321-1.728-1.809-2.974-3.56-2.974h-3.603c-1.052,0-2.054,0.447-2.735,1.246 c-0.689,0.791-0.995,1.848-0.834,2.893C24.051,75.129,35.331,85.59,49.189,86.43v2.035c0,0.816,0.463,1.564,1.194,1.928 c0.732,0.365,1.606,0.283,2.257-0.209l9.806-7.404c0.538-0.406,0.856-1.043,0.856-1.717C63.303,80.387,62.984,79.75,62.447,79.344 z"></path>
          <path d="M78.73,22.878c-0.699-0.058-1.342-0.416-1.76-0.98c-4.012-5.43-10.453-8.78-17.307-8.78c-0.436,0-0.872,0.014-1.309,0.04 c-0.58,0.035-1.152-0.138-1.617-0.488c-4.17-3.151-9.225-4.858-14.52-4.858c-9.481,0-17.896,5.498-21.82,13.822 c-0.352,0.746-1.056,1.263-1.873,1.377C8.074,24.463,0,33.46,0,44.308C0,55.233,8.205,64.238,18.77,65.59 c-0.285-1.098-0.536-2.209-0.708-3.348c-0.29-1.886,0.147-3.791,1.199-5.357l-1.123-0.004c-5.556-1.487-9.667-6.553-9.667-12.573 c0-7.186,5.846-13.03,13.032-13.03c0.363,0,0.72,0.029,1.074,0.058c2.07,0.159,3.946-1.184,4.45-3.191 c1.752-6.982,8-11.86,15.19-11.86c4.108,0,7.997,1.587,10.95,4.468c1.009,0.985,2.442,1.402,3.822,1.115 c0.879-0.183,1.778-0.274,2.673-0.274c5.029,0,9.521,2.826,11.725,7.379c0.773,1.601,2.465,2.555,4.235,2.373 c0.44-0.045,0.88-0.066,1.308-0.066c7.186,0,13.03,5.845,13.03,13.03c0,6.356-4.578,11.649-10.606,12.793 c0.432,1.446,0.787,2.929,1.017,4.452c0.212,1.378,0.039,2.768-0.479,4.025c10.457-1.451,18.541-10.422,18.541-21.271 C98.434,33.058,89.748,23.795,78.73,22.878z">
          </path>
        </svg>,
      callback: () => handlePublishProviderDBACatalogs(),
    },
    {
      svg:
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="24" height="24">
          <title>Reload this table</title>
          <path d="M21 3V8M21 8H16M21 8L18 5.29168C16.4077 3.86656 14.3051 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.2832 21 19.8675 18.008 20.777 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>,
      callback: () => handleReloadData(),
    }
  ];

  const handlePublishDBACatalogs = async () => {
    setIsPublishing(true);
    try {
      await dispatch(dbaCatalogs.resetAction({ actionType: 'create' }))
      await dispatch(dbaCatalogs.create({
        url: `https://catalog.digiit.${process.env.REACT_APP_API_URL}/api/v6.1.2/catalog/catalog-collections/management/dbas/publish`,
        entity: 'dbaCatalogs',
        jsonData:
        {
          "Context": {},
          "Records": [{}]
        }
      })).then(() => {
        fetchDBACatalogsList();
      });
    } catch (error) {
      console.error('Publish Error:', error);
    } finally {
      setIsPublishing(false);
    }
  }

  const handlePublishProviderDBACatalogs = async () => {
    await dispatch(dbaCatalogs.resetAction({ actionType: 'create' }))
    await dispatch(dbaCatalogs.create({
      url: `https://catalog.digiit.${process.env.REACT_APP_API_URL}/api/v6.1.2/catalog/catalog-collections/management/dbas/incremental-publish`,
      entity: 'dbaCatalogs',
      jsonData:
      {
        "Context": {},
        "Records": [{

        }]
      }
    })).then(() => {
      fetchDBACatalogsList();
    });
  }

  const handleReloadData = () => {
    fetchDBACatalogsList();
  }

  const handleSearch = (data: any) => {
    let searchCriteria = {};
    if (data?.column) {
      searchCriteria = [{
        FieldName: data?.column,
        FieldText: data?.column,
        FieldType: 'text',
        FilterOperatorId: 6,
        FieldValue: data?.value
      }]
    } else {
      searchCriteria = [{
        FieldName: "Name",
        FieldText: "Name",
        FieldType: 'text',
        FilterOperatorId: 6,
        FieldValue: data?.value
      }]
    }
    const hostname = getHostName();
    const hostUrl = getOrigin()
    const transaction_id = uuidv4();
    const guestTokenmetaData = storePersist.get("guestTokenMetadata").current?.result?.TokenMetadata;
    dispatch(dbaCatalogs.list({
      url: `https://catalog.digiit.${process.env.REACT_APP_API_URL}/api/v6.1.2/catalog/catalog-collections/management/dbas/fetch`, entity: 'dbaCatalogs',
      jsonData: {
        Context: {
          "domain": "Commerce",
          "action": "catalog",
          "core_version": "6.0",
          "bap_id": hostname || '',
          "bap_uri": hostUrl || '',
          "bapie_id": '',
          "bapie_uri": '',
          "bapnp_id": '',
          "bapnp_uri": '',
          "bpp_id": guestTokenmetaData?.TenantId,
          "bpp_uri": guestTokenmetaData?.CompanyName,
          "bppie_id": '',
          "bppie_uri": '',
          "bppnp_id": '',
          "bppnp_uri": '',
          "transaction_id": transaction_id,
          "message_id": uuidv4(),
          "city": guestTokenmetaData?.City,
          "country": guestTokenmetaData?.Country,
          "timestamp": moment().utc(),
          "ttl": "-1"
        },
        Records: [{}],
        Pagination:
        {
          "PageNumber": pageData,
          "PageSize": 25,
          "SortField": "DBAId",
          "SortOrder": "desc",
          "Searches": searchCriteria,
        }
      }
    }))
  }

  const getProductsFromCatalogs = () => {
    if (!dbaCatalogsItems?.result?.items) return [];

    const products = [];

    dbaCatalogsItems?.result?.items?.forEach((catalog: any) => {
      const dbaId = catalog?.DBAId;
      const dbaName = catalog?.Name;

      catalog?.Providers?.forEach((provider: any) => {
        const providerId = provider?.AccountId;
        const providerName = provider?.Name;

        provider?.Products?.forEach((product: any) => {
          products.push({
            DBAId: dbaId,
            DBAName: dbaName,
            ProviderId: providerId,
            ProviderName: providerName,
            ProductId: product?.ProductId,
            ProductName: product?.Name,
            Price: product?.Price,
            StockQuantity: product?.StockQuantity,
            Sku: product?.Sku,
            IsPublished: product?.IsPublished,
            ImageURL: product?.MediaMappings?.MediaFiles?.[0]?.FileMappingURL || null,
          });
        });
      });
    });

    return products;
  };

  return (
    <>
      {dbaCatalogsItems?.isLoading ? <PageLoader /> :
        <Table
          columnData={columnDefs}
          rowData={getProductsFromCatalogs()}
          setPageData={setPageData} setPageSize={setPageSize}
          paginationData={dbaCatalogsItems?.result?.pagination}
          enableFilter={true} onSearch={handleSearch}
          checkboxConfig={{
            allowMultiSelect: true,
            defaultSelectedIds: [],
            isDisabled: false,
            keyColumn: 'DBAId',
          }}
          additionButtons={additionButtons}
          headingName="Catalog Products"
        />
      }
    </>
  );
};

export default DBACatalogs;


